import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { verify2FA } from '../../services/auth'
import Logo from '../../components/Logo'
import Button from '../components/Button'
import Link from '../components/Link'

const Verify2FA: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Get email from location state
  const email = location.state?.email

  useEffect(() => {
    // If no email is provided, redirect to login
    if (!email) {
      navigate('/login')
    }
    // Focus first input on mount
    inputRefs.current[0]?.focus()
  }, [email, navigate])

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return
    }

    const newCode = [...code]
    newCode[index] = value
    setCode(newCode)
    setError('')

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').slice(0, 6)

    // Only allow digits
    if (!/^\d+$/.test(pastedData)) {
      return
    }

    const newCode = pastedData.split('')
    while (newCode.length < 6) {
      newCode.push('')
    }
    setCode(newCode)

    // Focus the last filled input or next empty one
    const nextIndex = Math.min(pastedData.length, 5)
    inputRefs.current[nextIndex]?.focus()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const fullCode = code.join('')
    if (fullCode.length !== 6) {
      setError('Please enter all 6 digits')
      return
    }

    setLoading(true)

    try {
      const response = await verify2FA(email, fullCode)

      if (response.token && response.user) {
        // Store token and user data
        localStorage.setItem('token', response.token)
        localStorage.setItem('user', JSON.stringify(response.user))

        // Redirect to home
        navigate('/')
        // Force page reload to update auth context
        window.location.reload()
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          'Invalid or expired verification code. Please try again.',
      )
      setCode(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="flex flex-col items-center">
          <Logo />
          <h3 className="mt-6 text-center text-2xl font-extrabold text-gray-900">
            Verify Your Identity
          </h3>
          <p className="mt-2 text-center text-sm text-gray-600">
            We've sent a 6-digit verification code to
            <br />
            <span className="font-medium text-gray-900">{email}</span>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-center">
              {error}
            </div>
          )}

          <div>
            <div className="flex justify-center gap-2">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className="w-12 h-14 text-center text-2xl font-bold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={loading}
                />
              ))}
            </div>
            <p className="mt-2 text-center text-xs text-gray-500">
              Enter the 6-digit code from your email
            </p>
          </div>

          <div>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              loading={loading}
              disabled={code.join('').length !== 6}
              className="disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying...' : 'Verify and Login'}
            </Button>
          </div>

          <div className="text-center text-sm">
            <p className="text-gray-600">
              Didn't receive the code?{' '}
              <Link to="/login" state={{ email }}>
                Try logging in again
              </Link>
            </p>
            <p className="mt-2 text-xs text-gray-500">
              The code expires in 10 minutes
            </p>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Verify2FA
